import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Container,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Drawer,
  IconButton,
  Tooltip
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import { ArrowLeft, Users, Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import AiChat from '../components/AiChat/AiChat';

export default function DossierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [negotiations, setNegotiations] = useState([]);
  const [negotiationsLoading, setNegotiationsLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesPage, setNotesPage] = useState(0);
  const [notesRowsPerPage, setNotesRowsPerPage] = useState(10);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksPage, setTasksPage] = useState(0);
  const [tasksRowsPerPage, setTasksRowsPerPage] = useState(10);
  const [taskFilter, setTaskFilter] = useState('all');
  const [aiOpen, setAiOpen] = useState(false);

  const totalTasks = tasks.length;
  const isTaskCompleted = (task) => {
    const value = task && task.eseguito;
    return value === 1 || value === '1';
  };
  const completedTasks = tasks.filter(isTaskCompleted).length;
  const pendingTasks = totalTasks - completedTasks;
  const priorityTasks = tasks.filter(task => task.prioritario).length;

  const filteredTasks = tasks.filter(task => {
    if (taskFilter === 'completed') return isTaskCompleted(task);
    if (taskFilter === 'pending') return !isTaskCompleted(task);
    if (taskFilter === 'priority') return task.prioritario;
    return true;
  });

  const getTaskDotColor = (task) => {
    if (isTaskCompleted(task)) return 'success.main';
    if (!task.data_scadenza) return null;
    const dueDate = new Date(task.data_scadenza);
    if (Number.isNaN(dueDate.getTime())) return null;
    const now = new Date();
    if (dueDate < now) return 'error.main';
    const diffMs = dueDate.getTime() - now.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays <= 3) return 'warning.main';
    return null;
  };
  const stripHtml = (html) => {
    if (!html || typeof html !== 'string') return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  useEffect(() => {
    const fetchDossier = async () => {
      try {
        const res = await api.get(`/dossier/${id}`);
        setDossier(res.data);
      } catch (error) {
        console.error('Error fetching dossier details:', error);
        toast.error('Errore nel caricamento dei dettagli Dossier');
      } finally {
        setLoading(false);
      }
    };

    const fetchNegotiations = async () => {
      setNegotiationsLoading(true);
      try {
        const res = await api.get(`/negotiation/dossier/${id}`);
        setNegotiations(res.data);
      } catch (error) {
        console.error('Error fetching negotiations:', error);
        // Don't toast error here to avoid spamming if just no negotiations or minor issue
      } finally {
        setNegotiationsLoading(false);
      }
    };

    const fetchNotes = async () => {
      setNotesLoading(true);
      try {
        const res = await api.get(`/dossier/${id}/notes`);
        setNotes(res.data || []);
      } catch (error) {
        console.error('Error fetching dossier notes:', error);
      } finally {
        setNotesLoading(false);
      }
    };

    const fetchTasks = async () => {
      setTasksLoading(true);
      try {
        const res = await api.get(`/dossier/${id}/tasks`);
        setTasks(res.data || []);
      } catch (error) {
        console.error('Error fetching dossier tasks:', error);
      } finally {
        setTasksLoading(false);
      }
    };

    if (id) {
      fetchDossier();
      fetchNegotiations();
      fetchNotes();
      fetchTasks();
    }
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    const lower = status.toLowerCase();
    
    // Success (Green)
    if (['completato', 'confermato', 'risolto', 'pagato', 'attivo', 'success', 'regolare'].some(k => lower.includes(k))) return 'success';
    
    // Error (Red)
    if (['errore', 'annullato', 'bloccato', 'rifiutato', 'chiuso', 'fallito', 'non_pervenuto'].some(k => lower.includes(k))) return 'error';
    
    // Warning (Orange)
    if (['in attesa', 'pending', 'da verificare', 'sospeso', 'da contattare', 'richiamare'].some(k => lower.includes(k))) return 'warning';
    
    // Info (Blue)
    if (['nuovo', 'bozza', 'in lavorazione', 'aperto', 'indeterminato'].some(k => lower.includes(k))) return 'info';
    
    return 'default';
  };

  const formatKey = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    // Check if date string
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    }
    if (typeof value === 'boolean') return value ? 'Sì' : 'No';
    return value;
  };

  // Define Tab Configuration
  const tabConfig = [
    {
      label: 'Dettagli Generali',
      fields: ['reference', 'description', 'status', 'rif_customer', 'tipo_contratto', 'created_at', 'updated_at']
    },
    {
      label: 'Finanziario',
      fields: ['importo_totale', 'acconto', 'percentage_transition', 'start_payment', 'number_installment', 'compenso_conferimento']
    },
    {
      label: 'Stato & Processo',
      fields: ['stato_preventivo', 'stato_contabilita', 'stato_contabilita_globale', 'data_invio_documenti', 'data_conferimento', 'data_chiusura_conferimento']
    },
    {
      label: 'Responsabili',
      fields: ['responsabile_commerciale', 'responsabile_contabile', 'responsabile_upsell']
    },
    {
      label: 'Note & Fallimento',
      fields: ['note', 'motivo_fallimento', 'motivazione_fallimento']
    },
    {
      label: 'Task & Promemoria',
      fields: []
    },
    {
      label: 'Sistema',
      fields: ['id', 'soft_delete']
    },
    {
      label: 'Pratiche',
      fields: []
    }
  ];

  // Helper to get fields for a specific tab
  const getFieldsForTab = (tabIndex) => {
    if (!dossier) return [];
    const config = tabConfig[tabIndex];
    if (!config) return []; 
    
    // For Responsabili tab, perform strict matching to avoid rif_ fields
    if (config.label === 'Responsabili') {
      return Object.keys(dossier).filter(key => 
        config.fields.includes(key.toLowerCase())
      );
    }
    
    return Object.keys(dossier).filter(key => 
      config.fields.includes(key.toLowerCase()) || 
      config.fields.some(field => key.toLowerCase().includes(field))
    );
  };

  // Helper to get remaining fields for "Altro"
  const getOtherFields = () => {
    if (!dossier) return [];
    const allConfiguredFields = tabConfig.flatMap(t => t.fields);
    return Object.keys(dossier).filter(key => {
      const lowerKey = key.toLowerCase();
      return !allConfiguredFields.some(field => lowerKey === field || lowerKey.includes(field));
    });
  };

  // Calculate visible tabs based on available data
  const visibleTabs = [
    ...tabConfig.map((tab, index) => ({
      ...tab,
      originalIndex: index,
      hasData: tab.label === 'Pratiche'
        ? true
        : tab.label === 'Note & Fallimento'
        ? getFieldsForTab(index).length > 0 || notes.length > 0
        : tab.label === 'Task & Promemoria'
        ? true
        : getFieldsForTab(index).length > 0
    })),
    {
      label: 'Altro',
      originalIndex: -1, // Special index for Altro
      hasData: getOtherFields().length > 0
    }
  ].filter(tab => tab.hasData);

  const currentTab = visibleTabs[tabValue];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!dossier) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6">Dossier non trovato</Typography>
        <Button startIcon={<ArrowLeft />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Torna indietro
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowLeft />}
            onClick={() => navigate('/dossier')}
            variant="outlined"
          >
            Torna alla lista
          </Button>
          <Typography variant="h4" component="h1">
            Dettagli Dossier: {dossier.reference}
          </Typography>
        </Box>
        <Tooltip title="Apri assistente AI per questo dossier">
          <IconButton
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
            onClick={() => setAiOpen(true)}
            size="large"
            aria-label="Assistente AI dossier"
          >
            <Sparkles size={18} />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card sx={{ mb: 3, p: 3, boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)', borderRadius: 4 }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <Users size={32} color="#637381" />
              </Grid>
              <Grid item xs>
                <Typography variant="subtitle2" color="text.secondary">Cliente</Typography>
                <Typography variant="h6">
                  {dossier.customer_ragione_sociale || 
                    ((dossier.customer_name || dossier.customer_surname) ? 
                      `${dossier.customer_name || ''} ${dossier.customer_surname || ''}`.trim() : 
                      `ID: ${dossier.rif_customer}`)}
                </Typography>
                {(dossier.customer_email || dossier.customer_telephone) && (
                  <Typography variant="body2" color="text.secondary">
                    {[dossier.customer_email, dossier.customer_telephone].filter(Boolean).join(' • ')}
                  </Typography>
                )}
              </Grid>
              <Grid item>
                <Button variant="outlined" onClick={() => navigate(`/customer/${dossier.rif_customer}`)}>
                  Vai al Cliente
                </Button>
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)', borderRadius: 4 }}>
            <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 500 }}>
              <Tabs 
                orientation="vertical"
                variant="scrollable"
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="dossier details tabs"
                sx={{ borderRight: 1, borderColor: 'divider', minWidth: 160, flexShrink: 0 }}
              >
                {visibleTabs.map((tab, index) => (
                  <Tab key={index} label={tab.label} sx={{ alignItems: 'flex-start', textAlign: 'left' }} />
                ))}
              </Tabs>

              <Box sx={{ p: 3, flexGrow: 1, width: '100%' }}>
            {/* Dynamic Tab Content */}
            {currentTab && currentTab.label === 'Pratiche' ? (
              <Box>
                {negotiationsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : negotiations.length === 0 ? (
                  <Typography variant="body1" color="text.secondary">
                    Nessuna negoziazione trovata per questo dossier.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Riferimento</TableCell>
                          <TableCell>Creditore</TableCell>
                          <TableCell>Categoria</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Data Apertura</TableCell>
                          <TableCell>Data Chiusura</TableCell>
                          <TableCell>Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {negotiations.map((neg) => (
                          <TableRow key={neg.id} hover>
                            <TableCell>{neg.id}</TableCell>
                            <TableCell>{neg.reference}</TableCell>
                            <TableCell>{neg.issuer_name || '-'}</TableCell>
                            <TableCell>{neg.category_name || '-'}</TableCell>
                            <TableCell>
                              <Chip 
                                label={neg.status} 
                                color={getStatusColor(neg.status)}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{neg.data_apertura ? new Date(neg.data_apertura).toLocaleDateString() : '-'}</TableCell>
                            <TableCell>{neg.data_chiusura ? new Date(neg.data_chiusura).toLocaleDateString() : '-'}</TableCell>
                            <TableCell>
                                <Button 
                                  size="small" 
                                  variant="outlined" 
                                  onClick={() => navigate(`/negotiation/${neg.id}`)}
                                >
                                  Vai
                                </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            ) : currentTab && currentTab.label === 'Note & Fallimento' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Grid container spacing={3}>
                  {getFieldsForTab(currentTab.originalIndex).map(key => (
                    <Grid item xs={12} md={6} key={key}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {formatKey(key)}
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {formatValue(dossier[key])}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Note collegate al Dossier
                  </Typography>
                  {notesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : notes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nessuna nota presente per questo dossier.
                    </Typography>
                  ) : (
                    <Box>
                      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Data</TableCell>
                              <TableCell>Utente</TableCell>
                              <TableCell>Importante</TableCell>
                              <TableCell>Appunta</TableCell>
                              <TableCell>Descrizione</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {notes
                              .slice(notesPage * notesRowsPerPage, notesPage * notesRowsPerPage + notesRowsPerPage)
                              .map((note) => (
                              <TableRow key={note.id} hover>
                                <TableCell>
                                  {note.created_at ? new Date(note.created_at).toLocaleString() : '-'}
                                </TableCell>
                                <TableCell>
                                  {[note.user_name, note.user_surname].filter(Boolean).join(' ') || `ID: ${note.rif_utente}`}
                                </TableCell>
                                <TableCell>
                                  {note.importante ? (
                                    <Chip label="Sì" color="warning" size="small" />
                                  ) : (
                                    <Chip label="No" variant="outlined" size="small" />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {note.appunta ? (
                                    <Chip label="Sì" color="info" size="small" />
                                  ) : (
                                    <Chip label="No" variant="outlined" size="small" />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {stripHtml(note.descrizione) || '-'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={notes.length}
                        rowsPerPage={notesRowsPerPage}
                        page={notesPage}
                        onPageChange={(e, newPage) => setNotesPage(newPage)}
                        onRowsPerPageChange={(e) => {
                          setNotesRowsPerPage(parseInt(e.target.value, 10));
                          setNotesPage(0);
                        }}
                        labelRowsPerPage="Righe per pagina:"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            ) : currentTab && currentTab.label === 'Task & Promemoria' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Task & Promemoria collegati al Dossier
                </Typography>
                {tasksLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                      <Chip
                        label={`Totale: ${totalTasks}`}
                        variant={taskFilter === 'all' ? 'filled' : 'outlined'}
                        color="default"
                        size="small"
                        onClick={() => {
                          setTaskFilter('all');
                          setTasksPage(0);
                        }}
                      />
                      <Chip
                        label={`Completati: ${completedTasks}`}
                        variant={taskFilter === 'completed' ? 'filled' : 'outlined'}
                        color="success"
                        size="small"
                        onClick={() => {
                          setTaskFilter('completed');
                          setTasksPage(0);
                        }}
                      />
                      <Chip
                        label={`Da fare: ${pendingTasks}`}
                        variant={taskFilter === 'pending' ? 'filled' : 'outlined'}
                        color="warning"
                        size="small"
                        onClick={() => {
                          setTaskFilter('pending');
                          setTasksPage(0);
                        }}
                      />
                      <Chip
                        label={`Prioritari: ${priorityTasks}`}
                        variant={taskFilter === 'priority' ? 'filled' : 'outlined'}
                        color="error"
                        size="small"
                        onClick={() => {
                          setTaskFilter('priority');
                          setTasksPage(0);
                        }}
                      />
                    </Box>
                    {!tasks.length ? (
                      <Typography variant="body2" color="text.secondary">
                        Nessun task presente per questo dossier.
                      </Typography>
                    ) : filteredTasks.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Nessun task per questo filtro.
                      </Typography>
                    ) : (
                      <Box>
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Scadenza</TableCell>
                                <TableCell>Mittente</TableCell>
                                <TableCell>Destinatario</TableCell>
                                <TableCell>Prioritario</TableCell>
                                <TableCell>Eseguito</TableCell>
                                <TableCell>Notifica</TableCell>
                                <TableCell>Titolo</TableCell>
                                <TableCell>Descrizione</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredTasks
                                .slice(tasksPage * tasksRowsPerPage, tasksPage * tasksRowsPerPage + tasksRowsPerPage)
                                .map((task) => {
                                  const dotColor = getTaskDotColor(task);
                                  let overdueDays = null;
                                  if (!isTaskCompleted(task) && task.data_scadenza) {
                                    const dueDate = new Date(task.data_scadenza);
                                    if (!Number.isNaN(dueDate.getTime()) && dueDate < new Date()) {
                                      const diffMs = new Date().getTime() - dueDate.getTime();
                                      const days = Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
                                      overdueDays = days;
                                    }
                                  }
                                  return (
                                <TableRow key={task.id} hover>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {dotColor && (
                                          <Box
                                            sx={{
                                              width: 10,
                                              height: 10,
                                              borderRadius: '50%',
                                              bgcolor: dotColor
                                            }}
                                          />
                                        )}
                                        {task.data_scadenza ? new Date(task.data_scadenza).toLocaleString() : '-'}
                                      </Box>
                                      {overdueDays !== null && (
                                        <Typography variant="caption" color="error.main">
                                          Scaduto da {overdueDays} giorni – verrà inoltrato alert all&apos;amministratore
                                        </Typography>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    {[task.mittente_name, task.mittente_surname].filter(Boolean).join(' ') || (task.rif_mittente ? `ID: ${task.rif_mittente}` : '-')}
                                  </TableCell>
                                  <TableCell>
                                    {[task.destinatario_name, task.destinatario_surname].filter(Boolean).join(' ') || (task.rif_utente ? `ID: ${task.rif_utente}` : '-')}
                                  </TableCell>
                                  <TableCell>
                                    {task.prioritario ? (
                                      <Chip label="Sì" color="error" size="small" />
                                    ) : (
                                      <Chip label="No" variant="outlined" size="small" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isTaskCompleted(task) ? (
                                      <Chip label="Completato" color="success" size="small" />
                                    ) : (
                                      <Chip label="Da fare" variant="outlined" size="small" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {task.enable_notifica ? (
                                      <Chip label="On" color="info" size="small" />
                                    ) : (
                                      <Chip label="Off" variant="outlined" size="small" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                      {task.titolo || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {stripHtml(task.descrizione) || '-'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          rowsPerPageOptions={[10, 25, 50]}
                          component="div"
                          count={filteredTasks.length}
                          rowsPerPage={tasksRowsPerPage}
                          page={tasksPage}
                          onPageChange={(e, newPage) => setTasksPage(newPage)}
                          onRowsPerPageChange={(e) => {
                            setTasksRowsPerPage(parseInt(e.target.value, 10));
                            setTasksPage(0);
                          }}
                          labelRowsPerPage="Righe per pagina:"
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ) : currentTab && (
              <Grid container spacing={3}>
                {(currentTab.originalIndex === -1 
                  ? getOtherFields() 
                  : getFieldsForTab(currentTab.originalIndex)
                ).map(key => (
                  <Grid item xs={12} md={6} key={key}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {formatKey(key)}
                    </Typography>
                    {['status', 'stato_preventivo', 'stato_contabilita', 'stato_contabilita_globale'].includes(key) ? (
                      <Chip 
                        label={dossier[key]} 
                        color={getStatusColor(dossier[key])} 
                        size="small" 
                        variant="outlined" 
                        sx={{ fontWeight: 'bold', mt: 0.5 }}
                      />
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: key === 'id' ? 'medium' : 'regular' }}>
                        {formatValue(dossier[key])}
                      </Typography>
                    )}
                  </Grid>
                ))}
              </Grid>
            )}
              </Box>
            </Box>

          </Card>
        </Grid>
      </Grid>

      <Drawer
        anchor="right"
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            p: 2,
            boxSizing: 'border-box'
          }
        }}
      >
        <AiChat
          title="Assistente AI – Dossier"
          subtitle="Chat contestuale basata sul dossier e relative pratiche."
          context={{ dossierId: Number(id) }}
        />
      </Drawer>
    </Container>
  );
}
